import { useMemo } from 'react';

import {
  FieldWrapper,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  TSysAgentStepDefinition,
  TSysAgentStepFormResponseSource,
  TSysAgentStepThreadSource,
  TSysAgentStepUploadSource,
  TWorkflowDefinition,
} from '@commenda/types';

import { WorkflowGraphHelper, WorkflowStepPickerHelper } from '../../helpers';
import { AgentFormResponsesField } from './AgentFormResponsesField';
import { AgentThreadSourceField } from './AgentThreadSourceField';
import { AgentUploadSourceField } from './AgentUploadSourceField';

interface IServiceAuditorNodePropertyPanelProps {
  workflow: TWorkflowDefinition | null;
  auditorStep: TSysAgentStepDefinition;
  onStepChange: (updates: Partial<TSysAgentStepDefinition>) => void;
}

export const ServiceAuditorNodePropertyPanel = ({
  workflow,
  auditorStep,
  onStepChange,
}: IServiceAuditorNodePropertyPanelProps) => {
  const draftStepOptions = useMemo(
    () =>
      WorkflowStepPickerHelper.buildStepPickerOptions(
        workflow,
        WorkflowGraphHelper.findUpstreamFormSteps(workflow, auditorStep.id),
        auditorStep.draftStepId ? [auditorStep.draftStepId] : []
      ),
    [workflow, auditorStep.id, auditorStep.draftStepId]
  );

  const upstreamDraftStepOptions = draftStepOptions.filter(
    (option) => !option.isStranded
  );
  const strandedDraftStepOptions = draftStepOptions.filter(
    (option) => option.isStranded
  );

  return (
    <div className="flex flex-col gap-xl">
      <FieldWrapper>
        <Label>What this step does</Label>
        <p className="txt-extra-small-regular text-text-secondary">
          Checks the vendor&apos;s draft against the workflow, the
          customer&apos;s answers, Commenda records and the request threads.
          Reports only mistakes it can prove. It never changes data.
        </p>
      </FieldWrapper>

      <FieldWrapper>
        <Label required>Draft comes from</Label>
        <Select
          value={auditorStep.draftStepId ?? ''}
          onValueChange={(draftStepId) => {
            if (draftStepId) onStepChange({ draftStepId });
          }}
          disabled={draftStepOptions.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose the step where the vendor uploads the draft" />
          </SelectTrigger>
          <SelectContent>
            {upstreamDraftStepOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
            {strandedDraftStepOptions.length > 0 && (
              <SelectGroup>
                <SelectLabel>
                  {WorkflowStepPickerHelper.STRANDED_STEP_GROUP_LABEL}
                </SelectLabel>
                {strandedDraftStepOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        <p className="txt-extra-small-regular mt-xs text-text-secondary">
          {upstreamDraftStepOptions.length === 0
            ? 'No upload step runs before this one. Connect one first.'
            : 'The auditor checks the newest file uploaded in this step.'}
        </p>
      </FieldWrapper>

      <p className="txt-extra-small-regular text-text-secondary">
        Form responses, uploads and threads are all fed in by default. Turn one
        off only if the auditor must not read it.
      </p>

      <AgentFormResponsesField
        workflow={workflow}
        agentStepId={auditorStep.id}
        source={auditorStep.formResponseSource}
        onChange={(formResponseSource: TSysAgentStepFormResponseSource) =>
          onStepChange({ formResponseSource })
        }
      />

      <AgentUploadSourceField
        workflow={workflow}
        agentStepId={auditorStep.id}
        source={auditorStep.uploadSource}
        onChange={(uploadSource: TSysAgentStepUploadSource) =>
          onStepChange({ uploadSource })
        }
      />

      <AgentThreadSourceField
        source={auditorStep.threadSource}
        onChange={(threadSource: TSysAgentStepThreadSource) =>
          onStepChange({ threadSource })
        }
      />
    </div>
  );
};
